import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Stack,
	TextField,
	Button,
	CircularProgress,
	IconButton,
	Box,
	Link,
} from '@mui/material';
import { getColorSettings } from '../../../../../../Utils/helper/commonUtils';
import { AppColors } from '../../../../../../Utils/helper/AppColors';
import { RefreshIcon, SecurityIcon } from '../../../../../../Components/Icons/Icons';
import useResponsive from '../../../../../../Utils/hooks/useResponsive';
// import CloseIcon from '@mui/icons-material/Close';

const VerifyIdentityCard = ({ open, onClose, onVerify, email, loading, onResendOtp }) => {
	const [otp, setOtp] = useState(['', '', '', '', '', '']);
	const [error, setError] = useState('');
	const [resendDisabled, setResendDisabled] = useState(false);
	const [countdown, setCountdown] = useState(0);

	const { small } = useResponsive();

	// Reset OTP when modal opens
	React.useEffect(() => {
		if (open) {
			setOtp(['', '', '', '', '', '']);
			setError('');
		}
	}, [open]);

	// Countdown timer for resend OTP
	React.useEffect(() => {
		let timer;
		if (countdown > 0) {
			timer = setTimeout(() => setCountdown(countdown - 1), 1000);
		} else {
			setResendDisabled(false);
		}
		return () => clearTimeout(timer);
	}, [countdown]);

	const handleResendOtp = () => {
		if (onResendOtp) {
			onResendOtp();
			setResendDisabled(true);
			setCountdown(90); // 90 seconds countdown
		}
	};

	// Handle OTP input change
	const handleChange = (index, value) => {
		if (/^\d?$/.test(value)) {
			const newOtp = [...otp];
			newOtp[index] = value;
			setOtp(newOtp);
			setError('');

			// Auto-focus next box if value entered
			if (value && index < 5) {
				document.getElementById(`otp-box-${index + 1}`)?.focus();
			}
		}
	};

	// Handle keydown for backspace navigation
	const handleKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !otp[index] && index > 0) {
			document.getElementById(`otp-box-${index - 1}`)?.focus();
		}
	};

	const handleSubmit = () => {
		if (otp.some((digit) => digit === '')) {
			setError('Please enter the complete 6-digit code');
			return;
		}
		const otpValue = otp.join('');
		onVerify(otpValue);
	};

	const localSettingColors = getColorSettings();

	return (
		<Dialog
			open={open}
			onClose={null}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '12px',
					width: { xs: '100vw', md: '670px' }, // Set the dialog width
					maxWidth: { xs: '100vw', md: '670px' }, // Ensure it doesn't exceed this width
					padding: { xs: '8px', md: '32px' },
				},
			}}
		>
			<DialogTitle
				sx={{ m: 0, padding: { xs: '16px 0', md: '32px 0' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
			>
				<SecurityIcon size={small ? 75 : 108} color={AppColors?.orange} />
			</DialogTitle>

			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<Typography sx={{ fontWeight: '700', fontSize: { xs: '20px', md: '32px' }, mb: 1 }}>Verify Your Identity</Typography>
					<Typography
						sx={{
							fontWeight: '400',
							fontSize: { xs: '14px', md: '18px' },
							textAlign: { xs: 'center', md: 'center' },
							color: AppColors?.grey_text,
							mb: 3,
						}}
					>
						Please enter the 6-digit code sent to your email address.
					</Typography>
					<Typography
						sx={{
							display: 'inline-block',
							fontWeight: '400',
							fontSize: '12px',
							color: AppColors?.white,
							backgroundColor: AppColors?.orange,
							borderRadius: '50px',
							px: 2,
							py: '4px',
							mb: 1,
							textAlign: 'center',
						}}
					>
						OTP sent to {!small ? '' : <br />} <strong>{email}</strong> {!small ? '' : <br />} Please check your inbox.
					</Typography>

					{/* Only this Box will scroll horizontally if needed */}
					<Box sx={{ width: {sm : 'auto', md : '100%'}, overflowX: 'auto', py: '32px' }}>
						<Stack direction="row" gap={{xs: '4px' , sm: '8px' , md: 2}} justifyContent="center" alignItems="center" sx={{ minWidth: 260 /* or as needed */ }}>
							{otp.map((digit, idx) => (
								<TextField
									key={idx}
									id={`otp-box-${idx}`}
									value={digit}
									onChange={(e) => handleChange(idx, e.target.value)}
									onKeyDown={(e) => handleKeyDown(idx, e)}
									onPaste={(e) => {
										const paste = e.clipboardData.getData('text').replace(/\D/g, '');
										if (paste.length === 6) {
											setOtp(paste.split('').slice(0, 6));
											setError('');
											e.preventDefault();
											setTimeout(() => {
												document.getElementById('otp-box-5')?.focus();
											}, 0);
										}
									}}
									sx={{
										'& .MuiInputBase-input': {
											padding: {xs : '5.5px 7px'},
										},
									}}
									inputProps={{
										maxLength: 1,
										style: {
											textAlign: 'center',
											fontSize: 16,
											width: small ? 24 : 30,
											height: small ? 24 : 30,
											backgroundColor: AppColors?.lightest_grey,
											border: `1px solid ${localSettingColors?.quaternary}`,
											borderRadius: '10px',
										},
									}}
									variant="outlined"
									size="small"
									disabled={loading}
									autoFocus={idx === 0}
								/>
							))}
						</Stack>
					</Box>

					{error && (
						<Typography color="error" variant="body2" textAlign="center">
							{error}
						</Typography>
					)}

					<Stack direction="row" justifyContent="center">
						<Typography
							sx={{
								fontWeight: 600,
								fontSize: 12,
								color: localSettingColors?.fontColor,
								display: 'flex',
								alignItems: 'center',
								gap: '6px',
							}}
						>
							<RefreshIcon size={20} color={AppColors?.orange} />
							{resendDisabled ? (
								<Typography component="span" color="text.secondary">
									Resend in {countdown}s
								</Typography>
							) : (
								<Link
									component="button"
									variant="body2"
									onClick={handleResendOtp}
									underline="hover"
									sx={{ cursor: 'pointer' , '&:hover': { color: AppColors?.orange } }}
									disabled={loading || resendDisabled}
								>
									Resend OTP
								</Link>
							)}
						</Typography>
					</Stack>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
				{/* <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button> */}
				<Button
					variant="contained"
					color="info"
					sx={{
						width: { xs: '100%', md: '400px' },
						height: '42px',
						border: loading || otp.some((d) => d === '') ? '0px' : '1px',
						paddingTop: 0,
					}}
					onClick={handleSubmit}
					disabled={loading || otp.some((d) => d === '')}
				>
					{loading ? 'Verifying...' : 'Verify OTP'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default VerifyIdentityCard;
